'use client'

import { useMemo, useState, JSX } from 'react'

import { ColumnDef, createColumnHelper } from '@tanstack/react-table'
import { Pencil, Trash2 } from 'lucide-react'
import { useRouter } from 'next/navigation'

import { Table } from '~/components/Table'
import {
  TABLE_ACTION_COLUMN_META,
  TABLE_ACTION_COLUMN_WIDTH,
  TableActionTrigger,
} from '~/components/TableActions'
import { withAuthentication } from '~/components/auth/withAuthentication'
import {
  Button,
  Copyable,
  NetworkDisplay,
  PageLayout,
  Timestamp,
  TokenDisplay,
  Tooltip,
} from '~/components/component-library'
import { AddressDisplay } from '~/components/component-library/AddressDisplay'
import { Dropdown } from '~/components/component-library/Dropdown'
import { DeleteContactDialog } from '~/components/contacts/DeleteContactDialog'
import { DebouncedInput } from '~/components/inputs/DebouncedInput'
import { useAuth } from '~/context/AuthProvider'
import { AddressBookData, useAddressBookData } from '~/hooks/useAddressBook'
import { ContactSendRule } from '~/types'

const ContactsPageInner = () => {
  const [globalFilter, setGlobalFilter] = useState('')

  const toolbar = (
    <div className="flex items-center gap-4">
      <div className="w-48 md:w-64">
        <DebouncedInput
          value={globalFilter ?? ''}
          onChange={(value) => setGlobalFilter(String(value))}
          placeholder="Search names or addresses..."
        />
      </div>
      <NewContactButton />
    </div>
  )

  return (
    <PageLayout pageType="table" title="Contacts" actionButtons={toolbar}>
      <AddressBook
        globalFilter={globalFilter}
        setGlobalFilter={setGlobalFilter}
      />
    </PageLayout>
  )
}

const NewContactButton = () => (
  <Button href={{ path: '/contacts/new' }}>Create contact</Button>
)

const AddressBook = ({
  globalFilter,
  setGlobalFilter,
}: {
  globalFilter: string
  setGlobalFilter: (value: string) => void
}): JSX.Element => {
  const columnHelper = createColumnHelper<AddressBookData>()

  const data = useAddressBookData()

  // The column only appears once there is something to show in it.
  const showRestrictions = data.some((entry) => entry.sendRules.length > 0)

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: ColumnDef<AddressBookData, any>[] = useMemo(() => {
    return [
      columnHelper.accessor('label', {
        header: 'Name',
        enableSorting: true,
        sortingFn: (a, b) => {
          // Convert both values to lowercase for case-insensitive comparison
          return a.original.label
            .toLowerCase()
            .localeCompare(b.original.label.toLowerCase())
        },
        cell: function NameCell(ctx) {
          const row = ctx.row.original
          return (
            <AddressDisplay
              address={row.address}
              layout="standard"
              isInternal
              popoverEditDisabled
              // Read from table state so the columns memo stays static
              highlightText={ctx.table.getState().globalFilter}
            />
          )
        },
        enableColumnFilter: false,
      }),
      columnHelper.accessor('address', {
        header: 'Address',
        size: 380,
        maxSize: 380,
        enableSorting: false,
        cell: function AddressCell(ctx) {
          return (
            <Copyable
              layout="standard"
              value={ctx.cell.getValue()}
              truncateMiddle={4}
            />
          )
        },
        enableColumnFilter: false,
      }),
      columnHelper.accessor(
        // A Date (not the ISO string) keeps the column out of the global
        // text filter, which only searches string and number values.
        (row) => new Date(row.updatedAt),
        {
          id: 'modified',
          header: 'Modified',
          size: 160,
          sortingFn: 'datetime',
          enableColumnFilter: false,
          cell: function ModifiedCell(ctx) {
            return (
              <Timestamp
                timestamp={ctx.row.original.updatedAt}
                type="relative"
              />
            )
          },
        },
      ),
      ...(showRestrictions
        ? [
            columnHelper.accessor('sendRules', {
              header: 'Restrictions',
              size: 200,
              enableSorting: false,
              enableColumnFilter: false,
              cell: function SendRulesCell(ctx) {
                return <SendRulesSummary sendRules={ctx.getValue()} />
              },
            }),
          ]
        : []),
      columnHelper.display({
        id: 'actions',
        ...TABLE_ACTION_COLUMN_WIDTH,
        cell: (ctx) => <ContactActionsMenu contact={ctx.row.original} />,
        meta: TABLE_ACTION_COLUMN_META,
      }),
    ]
    // Can't use columnHelper in memo because it's always different.
    // Adding it will trigger an infinite re-render of this component and its children.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showRestrictions])

  return (
    <Table
      columns={columns}
      data={data}
      showSorting
      state={{ globalFilter }}
      emptyMessage={`Your workspace has no entries${
        globalFilter ? ` matching "${globalFilter}"` : ''
      }`}
      setGlobalFilter={setGlobalFilter}
    />
  )
}

// One rule reads as the network or the token itself. More than one collapses
// to a count with the full list in a tooltip, because the column is too narrow.
const SendRulesSummary = ({ sendRules }: { sendRules: ContactSendRule[] }) => {
  if (sendRules.length === 0) return <span className="text-secondary">-</span>

  if (sendRules.length === 1) {
    const [rule] = sendRules
    return rule.tokenAddress ? (
      <TokenDisplay
        address={rule.tokenAddress}
        chainId={rule.chainId}
        layout="standard"
        size="small"
      />
    ) : (
      <NetworkDisplay layout="standard" size="small" chainId={rule.chainId} />
    )
  }

  // Rules are all-networks or all-tokens, never mixed.
  const summary = sendRules[0].tokenAddress
    ? `${sendRules.length} tokens`
    : `${sendRules.length} networks`

  return (
    <Tooltip
      content={
        // px/py even out the Tooltip's asymmetric px-2.5 py-1.5 padding
        <div className="flex flex-col gap-2.5 px-0.5 py-1.5">
          {sendRules.map((rule) =>
            rule.tokenAddress ? (
              <TokenDisplay
                key={`${rule.chainId}-${rule.tokenAddress}`}
                address={rule.tokenAddress}
                chainId={rule.chainId}
                layout="standard"
                size="small"
              />
            ) : (
              <NetworkDisplay
                key={rule.chainId}
                layout="standard"
                size="small"
                chainId={rule.chainId}
              />
            ),
          )}
        </div>
      }
    >
      {/* A button so keyboard focus can reveal the tooltip */}
      <button
        type="button"
        className="cursor-default underline decoration-dashed underline-offset-4"
      >
        {summary}
      </button>
    </Tooltip>
  )
}

const ContactActionsMenu = ({ contact }: { contact: AddressBookData }) => {
  const router = useRouter()
  const { setGlobalModalState, closeGlobalModal } = useAuth()

  return (
    <div className="flex justify-end">
      <Dropdown
        items={[
          {
            icon: <Pencil />,
            content: 'Edit',
            onClick: () => router.push(`/contacts/${contact.address}`),
          },
          {
            icon: <Trash2 />,
            content: 'Delete',
            onClick: () =>
              setGlobalModalState({
                isOpen: true,
                content: (
                  <DeleteContactDialog
                    key={contact.address}
                    contact={contact}
                    onClose={closeGlobalModal}
                  />
                ),
              }),
          },
        ]}
      >
        <TableActionTrigger aria-label="Contact actions" />
      </Dropdown>
    </div>
  )
}
export const ContactsPage = withAuthentication(ContactsPageInner)
